import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faUsers,
  faBuilding,
  faMapMarkerAlt,
  faNetworkWired,
  faPercent,
  faBox,
  faTags,
  faBarcode,
  faBars,
  faChevronLeft,
  faChevronRight,
  faTimes,
  faSignOutAlt,
  faUser,
  faFileAlt,
  faFolder,
  faShoppingCart,
  faClipboardCheck,
  faUserTie,
  faFileInvoiceDollar,
  faSlidersH,
  faHourglassHalf,
  faArrowRightArrowLeft,
  faRobot,
  faClipboardList,
} from '@fortawesome/free-solid-svg-icons';
import { useRoleAccess } from '../hooks/useRoleAccess';
import useAuth from '../hooks/useAuth';
import { UI_COLORS, GRADIENTS, colorHelpers } from '../utils/colors';
import { PERMISSIONS, USER_ROLES } from '../utils/constants';
import erpLogo from '../assets/erp_logo.jpeg';
import { useTranslation } from 'react-i18next';
import DeveloperCredit from './common/DeveloperCredit';
import SearchableSelect from './common/SearchableSelect';
import { useWarehouseContext } from '../context/WarehouseContext';



/**
 * Side Navigation Bar Component
 * Persistent sidebar navigation with role-based menu items
 */
const SideNavbar = ({ isMobileMenuOpen, setIsMobileMenuOpen, currentPage = 'dashboard', setCurrentPage }) => {
  // Only allow collapse on true desktop screens (>= 1280px)
  // For mobile overlay mode (1030px-1280px), sidebar should always be uncollapsed
  // Default collapsed state: open (not collapsed) on desktop by default.
  // Keep SSR-safe guard when window is not defined.
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    // Open (uncollapsed) by default on desktop (>= 1280px). Mobile overlay also starts uncollapsed.
    return false;
  });

  // Auto-collapse/expand on window resize - only allow on desktop (>= 1280px)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onResize = () => { // Effect: updates `isCollapsed` on window resize so sidebar layout adapts.
      const width = window.innerWidth;
      // Only allow collapse on true desktop screens (>= 1280px)
      // For mobile overlay mode (< 1280px), always keep uncollapsed
      if (width >= 1280) {
        // On desktop, user can manually control collapse state - don't auto-collapse
        // The collapse state is controlled by user clicking the collapse button
      } else {
        // Force uncollapsed state for mobile overlay mode
        setIsCollapsed(false);
      }
    };

    window.addEventListener('resize', onResize);
    // ensure initial state reflects current viewport
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const { userRole, isSuperAdmin, checkPermission } = useRoleAccess();
  const { logout, user } = useAuth();
  const { warehouses, selectedWarehouseIds, setWarehouseIds, clearSelection, loading: warehousesLoading } = useWarehouseContext();
  const { t } = useTranslation();
  const strings = t('navigation', { returnObjects: true });

  // Provide a local fallback for mobile menu state when parent doesn't control it
  const [localMobileOpen, setLocalMobileOpen] = useState(false);
  const mobileOpen = typeof isMobileMenuOpen === 'boolean' ? isMobileMenuOpen : localMobileOpen;
  const setMobileOpen = setIsMobileMenuOpen || setLocalMobileOpen;
  // Track which nav with children is expanded (for tap on mobile)
  const [expandedMenu, setExpandedMenu] = useState(null);
  // Track if we're on mobile/touch device
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  // Track if sub-menu was manually toggled to disable hover
  // eslint-disable-next-line
  const [manuallyToggled, setManuallyToggled] = useState(null);
  // Track hovered item for tooltip when collapsed
  const [hoveredItem, setHoveredItem] = useState(null);
  const [hoveredItemPosition, setHoveredItemPosition] = useState({ top: 0 });

  // Ref for sidebar container to detect outside clicks
  // No longer using a ref for outside-click detection; we'll rely on
  // event.target.closest('.sidebar-container') which works reliably
  // for this single-instance sidebar and avoids an extra ref.

  useEffect(() => {
    const checkTouchDevice = () => {
      setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
    };
    checkTouchDevice();
  }, []);

  // Close sub-menus when sidebar is collapsed
  useEffect(() => {
    if (isCollapsed) {
      setExpandedMenu(null);
      setManuallyToggled(null);
    }
  }, [isCollapsed, setExpandedMenu, setManuallyToggled]);

  // Sub-menu stays open when clicking outside the sidebar.
  // It only closes when another sidebar nav item is clicked (handled in the nav item click logic).

  // Navigation items with role restrictions and Font Awesome icons
  const navigationItems = [
    {
      id: 'dashboard',
      label: strings.pages.dashboard,
      icon: faChartLine,
      path: '/dashboard',
      requiresSuperAdmin: false,
      requiredPermission: PERMISSIONS.VIEW_DASHBOARD,
      excludedRoles: [USER_ROLES.ACCOUNT_USER],
      children: [
        // {
        //   id: 'dashboard-overview',
        //   label: strings.pages.overview,
        //   icon: faChartLine,
        //   path: '/dashboard', // Main dashboard view
        //   requiresSuperAdmin: false,
        //   requiredPermission: null,
        // },
        {
          id: 'dashboard-sales',
          label: strings.pages.sales,
          icon: faChartLine,
          path: '/dashboard/sales',
          requiresSuperAdmin: false,
          requiredPermission: null,
          excludedRoles: [USER_ROLES.DELIVERY_SUPERVISOR],
        },
        {
          id: 'dashboard-delivery',
          label: strings.pages.delivery,
          icon: faBox, // Using faBox as a placeholder for delivery
          path: '/dashboard/delivery',
          requiresSuperAdmin: false,
          requiredPermission: null,
          excludedRoles: [USER_ROLES.DELIVERY_SUPERVISOR],
        },
        {
          id: 'dashboard-purchase',
          label: strings.pages.purchase,
          icon: faShoppingCart,
          path: '/dashboard/purchase',
          requiresSuperAdmin: false,
          requiredPermission: null,
          excludedRoles: [USER_ROLES.DELIVERY_SUPERVISOR],
        },
        {
          id: 'dashboard-challan-analytics',
          label: strings.pages.challan,
          icon: faFileInvoiceDollar,
          path: '/dashboard/challan-analytics',
          requiresSuperAdmin: false,
          requiredPermission: null,
          excludedRoles: [USER_ROLES.DELIVERY_SUPERVISOR, USER_ROLES.SALES_STAFF, USER_ROLES.ACCOUNT_USER],
        },
        {
          id: 'dashboard-assigned-to-challan',
          label: strings.pages.operations,
          icon: faHourglassHalf,
          path: '/dashboard/assigned-to-challan',
          requiresSuperAdmin: false,
          requiredPermission: null,
          excludedRoles: [USER_ROLES.DELIVERY_SUPERVISOR, USER_ROLES.SALES_STAFF, USER_ROLES.ACCOUNT_USER],
        },
        {
          id: 'dashboard-movement-analytics',
          label: strings.pages.movement,
          icon: faArrowRightArrowLeft,
          path: '/dashboard/movement-analytics',
          requiresSuperAdmin: false,
          requiredPermission: null,
          excludedRoles: [USER_ROLES.DELIVERY_SUPERVISOR, USER_ROLES.SALES_STAFF, USER_ROLES.ACCOUNT_USER],
        },
        {
          id: 'dashboard-inventory',
          label: strings.pages.inventory, // 'Inventory' exists
          icon: faBox,
          path: '/dashboard/inventory',
          requiresSuperAdmin: false,
          requiredPermission: null,
          excludedRoles: [USER_ROLES.DELIVERY_SUPERVISOR],
        },
        {
          id: 'dashboard-customers',
          label: strings.pages.customers, // 'Customers' exists
          icon: faUsers,
          path: '/dashboard/customers',
          requiresSuperAdmin: false,
          requiredPermission: null,
          excludedRoles: [USER_ROLES.DELIVERY_SUPERVISOR],
        },
        {
          id: 'dashboard-inquiries',
          label: strings.pages.inquiries, // 'Inquiries' exists
          icon: faTags,
          path: '/dashboard/inquiries',
          requiresSuperAdmin: false,
          requiredPermission: null,
          excludedRoles: [USER_ROLES.DELIVERY_SUPERVISOR],
        },
        {
          id: 'dashboard-financial',
          label: strings.pages.financial,
          icon: faFileAlt, // Using faFileAlt for Financial
          path: '/dashboard/financial',
          requiresSuperAdmin: false,
          requiredPermission: null,
          excludedRoles: [USER_ROLES.DELIVERY_SUPERVISOR],
        },
        {
          id: 'dashboard-supervisor',
          label: strings.pages.supervisorStaff,
          icon: faUserTie,
          path: '/dashboard/supervisor',
          requiresSuperAdmin: false,
          requiredPermission: null,
          excludedRoles: [],
        },
        {
          id: 'dashboard-sales-analytics',
          label: strings.pages.salesStaff,
          icon: faChartLine,
          path: '/dashboard/sales-analytics',
          requiresSuperAdmin: false,
          requiredPermission: null,
          excludedRoles: [USER_ROLES.DELIVERY_SUPERVISOR, USER_ROLES.SALES_STAFF, USER_ROLES.ACCOUNT_USER],
        },
      ],
    },
    {
      id: 'users',
      label: strings.pages.users,
      icon: faUsers,
      path: '/users',
      requiresSuperAdmin: false,
      requiredPermission: PERMISSIONS.VIEW_USERS,
      excludedRoles: [USER_ROLES.ACCOUNT_USER],
    },
    {
      id: 'customers',
      label: strings.pages.customers,
      icon: faBuilding,
      path: '/customers',
      requiresSuperAdmin: false,
      requiredPermission: PERMISSIONS.VIEW_CUSTOMERS,
      excludedRoles: [USER_ROLES.ACCOUNT_USER],
    },
    {
      id: 'inquiries',
      label: strings.pages.inquiries,
      icon: faTags,
      path: '/inquiries',
      requiresSuperAdmin: false,
      requiredPermission: PERMISSIONS.VIEW_INQUIRIES,
      excludedRoles: [USER_ROLES.ACCOUNT_USER],
    },
    {
      id: 'order',
      label: strings.pages.deliveryOrderLabel,
      icon: faShoppingCart,
      path: '/order',
      requiresSuperAdmin: false,
      requiredPermission: PERMISSIONS.VIEW_ORDERS,
      children: [
        {
          id: 'orders',
          label: strings.pages.ordersLabel,
          icon: faShoppingCart,
          path: '/order/orders',
          requiresSuperAdmin: false,
          requiredPermission: PERMISSIONS.VIEW_ORDERS,
          excludedRoles: [USER_ROLES.ACCOUNT_USER, USER_ROLES.DELIVERY_SUPERVISOR],
        },
        {
          id: 'confirmed-orders',
          label: t('commonCols.confirmedOrders'),
          icon: faClipboardCheck,
          path: '/order/confirmed-orders',
          requiresSuperAdmin: false,
          requiredPermission: PERMISSIONS.VIEW_ORDERS,
          excludedRoles: [USER_ROLES.ACCOUNT_USER, USER_ROLES.DELIVERY_SUPERVISOR],
        },
        {
          id: 'delivery-orders',
          label: t('deliveryOrders.page.header.title'),
          icon: faBox,
          path: '/order/delivery-orders',
          requiresSuperAdmin: false,
          requiredPermission: PERMISSIONS.VIEW_ORDERS,
          excludedRoles: [],
        },
        {
          id: 'delivery-challan',
          label: strings.pages.deliveryChallanNav,
          icon: faFileAlt,
          path: '/order/delivery-challan',
          requiresSuperAdmin: false,
          requiredPermission: PERMISSIONS.VIEW_ORDERS,
          excludedRoles: [],
        },
        {
          id: 'delivery-challan-printed',
          label: strings.pages.deliveryChallanPrinted,
          icon: faClipboardCheck,
          path: '/order/delivery-challan-printed',
          requiresSuperAdmin: false,
          requiredPermission: PERMISSIONS.VIEW_ORDERS,
          excludedRoles: [USER_ROLES.DELIVERY_SUPERVISOR],
        },

        {
          id: 'invoice-sales',
          label: strings.pages.invoiceSales,
          // Combined entry for invoice and sales daybook
          icon: faFileAlt,
          path: '/order/invoice',
          requiresSuperAdmin: false,
          requiredPermission: PERMISSIONS.VIEW_ORDERS,
          excludedRoles: [USER_ROLES.DELIVERY_SUPERVISOR],
        },
        {
          id: 'sales-daybook',
          label: strings.pages.salesDaybook,
          icon: faFileAlt,
          path: '/order/sales-daybook',
          requiresSuperAdmin: false,
          requiredPermission: PERMISSIONS.VIEW_ORDERS,
          excludedRoles: [USER_ROLES.DELIVERY_SUPERVISOR],
        },
        // {
        //   id: 'order-reports',
        //   label: strings.pages.reports,
        //   icon: faChartLine,
        //   path: '/order/reports',
        //   requiresSuperAdmin: true,
        //   requiredPermission: null,
        // },
      ],
    },
    {
      id: 'purchase',
      label: strings.pages.purchaseOrders,
      icon: faShoppingCart,
      path: '/purchase',
      requiresSuperAdmin: false,
      requiredPermission: PERMISSIONS.VIEW_PURCHASE,
      children: [
        {
          id: 'purchase-orders',
          label: strings.pages.purchaseOrders,
          icon: faShoppingCart,
          path: '/purchase-orders',
          requiresSuperAdmin: false,
          requiredPermission: PERMISSIONS.VIEW_PURCHASE,
          excludedRoles: [USER_ROLES.DELIVERY_SUPERVISOR, USER_ROLES.ACCOUNT_USER],
        },
        {
          id: 'purchase-advice',
          label: strings.pages.purchaseAdvice,
          icon: faFileAlt,
          path: '/purchase/advice',
          requiresSuperAdmin: false,
          requiredPermission: PERMISSIONS.VIEW_PURCHASE_ADVICE,
          excludedRoles: [USER_ROLES.ACCOUNT_USER],
        },
        {
          id: 'purchase-challan',
          label: t('navigation.pages.purchaseChallan'),
          icon: faBarcode,
          path: '/purchase-challan',
          requiresSuperAdmin: false,
          requiredPermission: PERMISSIONS.VIEW_PURCHASE_CHALLAN,
          excludedRoles: [USER_ROLES.DELIVERY_SUPERVISOR],
        },
        {
          id: 'purchase-challan-printed',
          label: strings.pages.purchaseChallanPrinted,
          icon: faClipboardCheck,
          path: '/purchase-challan-printed',
          requiresSuperAdmin: false,
          requiredPermission: PERMISSIONS.VIEW_PURCHASE_CHALLAN,
          excludedRoles: [USER_ROLES.DELIVERY_SUPERVISOR],
        },
        {
          id: 'invoice-purchase',
          label: t('navigation.pages.invoicePurchase'),
          icon: faFileAlt,
          path: '/invoice-purchase',
          requiresSuperAdmin: false,
          requiredPermission: PERMISSIONS.VIEW_PURCHASE_DAYBOOK,
          excludedRoles: [USER_ROLES.DELIVERY_SUPERVISOR],
        },
        {
          id: 'purchase-daybook',
          label: t('navigation.pages.purchaseDaybook'),
          icon: faFileAlt,
          path: '/purchase-daybook',
          requiresSuperAdmin: false,
          requiredPermission: PERMISSIONS.VIEW_PURCHASE_DAYBOOK,
          excludedRoles: [USER_ROLES.DELIVERY_SUPERVISOR],
        },
      ],
    },
    {
      id: 'products',
      label: strings.pages.products,
      icon: faBox,
      path: '/products',
      requiresSuperAdmin: false,
      requiredPermission: PERMISSIONS.VIEW_PRODUCTS,
      excludedRoles: [USER_ROLES.ACCOUNT_USER],
      // children are shown on hover (desktop) or tap (mobile) and navigate normally
      children: [
        {
          id: 'product-categories',
          label: strings.pages.category,
          icon: faFolder,
          path: '/products/categories',
          requiresSuperAdmin: false,
          requiredPermission: PERMISSIONS.VIEW_PRODUCTS,
        },
        {
          id: 'product-catalogue',
          label: strings.pages.subCategory,
          // Use a box icon for the catalogue to represent product items
          icon: faBox,
          path: '/products/catalogue',
          requiresSuperAdmin: false,
          requiredPermission: PERMISSIONS.VIEW_PRODUCTS,
        },
        {
          id: 'product-sizes',
          label: strings.pages.product,
          icon: faShoppingCart,
          path: '/products/sizes',
          requiresSuperAdmin: false,
          requiredPermission: PERMISSIONS.VIEW_PRODUCTS,
        },
      ],
    },
    {
      id: 'vehicle-tracking',
      label: t('commonCols.vehicleTracking'),
      icon: faMapMarkerAlt,
      path: '/vehicle-tracking',
      requiresSuperAdmin: false,
      requiredPermission: PERMISSIONS.VIEW_ORDERS,
      excludedRoles: [USER_ROLES.DELIVERY_SUPERVISOR, USER_ROLES.ACCOUNT_USER],
    },

    {
      id: 'warehouses',
      label: strings.pages.warehouses,
      icon: faBuilding,
      path: '/warehouses',
      requiresSuperAdmin: false,
      requiredPermission: PERMISSIONS.VIEW_WAREHOUSES,
      excludedRoles: [USER_ROLES.ACCOUNT_USER],
      // no children here; inventory is a top-level navigation item
    },
    {
      id: 'warehouse-inventory',
      label: strings.pages.inventory,
      icon: faBox,
      path: '/warehouse-inventory',
      requiresSuperAdmin: false,
      requiredPermission: PERMISSIONS.VIEW_INVENTORY,
      excludedRoles: [USER_ROLES.ACCOUNT_USER],
    },
    {
      id: 'locations',
      label: strings.pages.locations,
      icon: faMapMarkerAlt,
      path: '/locations',
      requiresSuperAdmin: false,
      requiredPermission: PERMISSIONS.VIEW_LOCATIONS,
      excludedRoles: [USER_ROLES.ACCOUNT_USER],
    },
    {
      id: 'taxes',
      label: strings.pages.taxes,
      icon: faPercent,
      path: '/taxes',
      requiresSuperAdmin: false,
      requiredPermission: PERMISSIONS.VIEW_TAXES,
      excludedRoles: [USER_ROLES.ACCOUNT_USER],
    },
    {
      id: 'hsn-codes',
      label: strings.pages.hsnCodes,
      icon: faBarcode,
      path: '/hsn-codes',
      requiresSuperAdmin: false,
      requiredPermission: PERMISSIONS.VIEW_HSN_CODES,
      excludedRoles: [USER_ROLES.ACCOUNT_USER],
    },
    {
      id: 'ip-addresses',
      label: strings.pages.ipAddresses,
      icon: faNetworkWired,
      path: '/ip-addresses',
      requiresSuperAdmin: false,
      requiredPermission: PERMISSIONS.VIEW_IP_ADDRESSES,
    },
    {
      id: 'ai',
      label: strings.pages.ai,
      icon: faRobot,
      path: '/ai',
      requiresSuperAdmin: false,
      requiredPermission: PERMISSIONS.VIEW_AI_ADVISOR,
      excludedRoles: [
        USER_ROLES.OPERATIONS_MANAGER,
        USER_ROLES.SALES_STAFF,
        USER_ROLES.DELIVERY_SUPERVISOR,
        USER_ROLES.ACCOUNT_USER,
        USER_ROLES.LABOUR,
      ],
      children: [
        {
          id: 'ai-chat',
          label: strings.pages.aiChat,
          icon: faRobot,
          path: '/ai/chat',
          requiresSuperAdmin: false,
          requiredPermission: PERMISSIONS.VIEW_AI_ADVISOR,
        },
        {
          id: 'ai-purchase-plan',
          label: strings.pages.aiPurchasePlan,
          icon: faClipboardList,
          path: '/ai/purchase-plan',
          requiresSuperAdmin: false,
          requiredPermission: PERMISSIONS.VIEW_AI_ADVISOR,
        },
      ],
    },
    {
      id: 'settings',
      label: strings.pages.settings || 'Settings',
      icon: faSlidersH,
      path: '/settings',
      requiresSuperAdmin: true,
      requiredPermission: null,
      children: [
        {
          id: 'channel-settings',
          label: strings.pages.channelSettings || 'Channel Settings',
          icon: faSlidersH,
          path: '/settings/channel',
          requiresSuperAdmin: true,
          requiredPermission: null,
        },
        {
          id: 'tally-settings',
          label: strings.pages.tallySettings || 'Tally Settings',
          icon: faFileInvoiceDollar,
          path: '/settings/tally',
          requiresSuperAdmin: true,
          requiredPermission: null,
        },
      ],
    },
  ];

  const handleLogout = async () => {
    await logout();
  };

  /** Handle navigation clicks from the sidebar. */
  const handleNavigation = (pageId) => { // If a parent `setCurrentPage` is provided, call it; otherwise dispatch a custom event.
    let targetPageId = pageId;
    if (pageId === 'dashboard' && userRole === USER_ROLES.DELIVERY_SUPERVISOR) {
      targetPageId = 'dashboard-supervisor';
    }

    // Special-case: products opens a right-side sub-menu instead of navigating to a page
    if (pageId === 'products') {
      if (typeof window !== 'undefined' && window.dispatchEvent) {
        window.dispatchEvent(new CustomEvent('openRightPanel', { detail: { panel: 'products' } }));
      }
      // On touch devices, don't close the mobile menu when opening products panel
      if (!isTouchDevice && setMobileOpen) setMobileOpen(false);
      return;
    }

    // Hide any open sub-menus when navigating to another page
    setExpandedMenu(null);
    setManuallyToggled(null);

    if (setCurrentPage) {
      setCurrentPage(targetPageId);
      // Close mobile menu when navigating on non-touch devices
      // On touch devices, let user manually close or keep it open
      if (!isTouchDevice && setMobileOpen) setMobileOpen(false);
    }
  };

  /** Handle navigation clicks from child menu items - keeps submenu open */
  const handleChildNavigation = (pageId) => {
    if (setCurrentPage) {
      setCurrentPage(pageId);
      // Don't close the submenu when navigating to a child
      // Close mobile menu when navigating on non-touch devices
      if (!isTouchDevice && setMobileOpen) setMobileOpen(false);
    }
  };

  return (
    <>
      {/* Mobile top navbar: show hamburger on small screens to open the side menu */}
      <div className="xl:hidden fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-3 py-2"
        style={{ backgroundColor: UI_COLORS.BACKGROUNDS.HEADER, boxShadow: `0 2px 8px ${UI_COLORS.SHADOWS.STRONG}` }}>
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? strings.sidebar.mobile.closeMenu : strings.sidebar.mobile.openMenu}
          className="p-2 rounded-md focus:outline-none"
        >
          <FontAwesomeIcon icon={mobileOpen ? faTimes : faBars} style={{ color: UI_COLORS.TEXT.ON_THEME }} />
        </button>

        <div className="text-sm font-semibold truncate" style={{ color: UI_COLORS.TEXT.ON_THEME }}>
          {!isCollapsed ? (
            <img src={erpLogo} alt={strings.appName || 'ERP'} className="h-6 object-contain" />
          ) : ''}
        </div>

        {/* spacer to balance layout */}
        <div style={{ width: 36 }} />
      </div>
      {/* Overlay for mobile when sidebar is open */}
      <div
        className={`fixed inset-0 z-40 xl:hidden ${mobileOpen ? 'block' : 'hidden'
          }`}
        style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
        onClick={() => setMobileOpen && setMobileOpen(false)}
      ></div>

      {/* Side Navigation */}
      <div
        className={`sidebar-container fixed left-0 top-0 h-full transition-all duration-300 z-50 shadow-2xl ${isCollapsed ? 'w-64' : 'w-217'
          } xl:relative xl:translate-x-0 ${!mobileOpen ? '-translate-x-full xl:translate-x-0' : 'translate-x-0'
          }`}
        style={{
          // Use a solid sidebar background color to avoid translucency on mobile
          background: GRADIENTS.THEME,
          backgroundColor: UI_COLORS.BACKGROUNDS.HEADER,
          boxShadow: `4px 0 20px ${UI_COLORS.SHADOWS.STRONG}`,
          // Constrain to viewport height on mobile and allow internal scrolling
          maxHeight: '100vh',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          // Hide scrollbar for all browsers
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE and Edge
          // Fix explicit widths to avoid unexpected layout shifts on some pages
          width: isCollapsed ? '64px' : '217px',
          minWidth: isCollapsed ? '64px' : '217px',
          maxWidth: isCollapsed ? '64px' : '217px',
          boxSizing: 'border-box',
        }}
      >
        <style>{`
        .sidebar-container::-webkit-scrollbar { display: none; }
        .nav-menu::-webkit-scrollbar { display: none; }
        .sidebar-container { scrollbar-width: none; -ms-overflow-style: none; }
        .nav-menu { scrollbar-width: none; -ms-overflow-style: none; }
      `}</style>
        <div className="flex flex-col h-full w-full backdrop-blur-sm">
          {/* Header */}
          <div
            className="flex items-center justify-between p-4 border-b"
            style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.TEXT.ON_THEME, 0.2) }}
          >
            {!isCollapsed && (
              <div className="flex items-center space-x-3">
                <h1
                  className="text-xl font-bold tracking-wide"
                  style={{ color: UI_COLORS.TEXT.ON_THEME }}
                >
                  <img src={erpLogo} alt={strings.appName || 'ERP'} className="ml-1 w-32 object-contain" />
                </h1>
              </div>
            )}
            <button
              onClick={() => {
                // Only allow collapse on desktop (>= 1280px - xl breakpoint)
                if (window.innerWidth >= 1280) {
                  setIsCollapsed(!isCollapsed);
                }
              }}
              className="p-2 rounded-lg transition-all duration-200 hover:scale-105 hidden xl:block"
              style={{
                color: UI_COLORS.TEXT.ON_THEME,
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.ON_THEME, 0.1),
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.ON_THEME, 0.2);
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.ON_THEME, 0.1);
              }}
              title={isCollapsed ? strings.sidebar.collapse.expand : strings.sidebar.collapse.collapse}
            >
              <FontAwesomeIcon
                icon={faChevronLeft}
                className="w-4 h-4 transition-transform duration-200"
                style={{
                  transform: isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)',
                  color: UI_COLORS.TEXT.ON_THEME
                }}
              />
            </button>
          </div>

          {/* User Info */}
          <div
            className="p-4 border-b"
            style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.TEXT.ON_THEME, 0.2) }}
          >
            <div className="flex items-center space-x-3">
              {/* User Avatar */}
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm shadow-lg"
                style={{
                  background: GRADIENTS.TERTIARY,
                  color: UI_COLORS.TEXT.PRIMARY
                }}
              >
                {user?.name ? user.name.charAt(0).toUpperCase() : (
                  <FontAwesomeIcon
                    icon={faUsers}
                    className="text-sm"
                  />
                )}
              </div>

              {!isCollapsed && (
                <div className="flex-1 min-w-0">
                  <p
                    className="text-sm font-semibold truncate"
                    style={{ color: UI_COLORS.TEXT.ON_THEME }}
                  >
                    {user?.name || user?.email || strings.sidebar.userFallback}
                  </p>
                  <p
                    className="text-xs truncate opacity-80"
                    style={{ color: UI_COLORS.TEXT.ON_THEME }}
                  >
                    {userRole 
                      ? (t('users.forms.userTypes', { returnObjects: true })?.find(ut => ut.value === userRole)?.label || userRole.replace('_', ' '))
                      : strings.sidebar.loading}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Navigation Menu */}
          <nav className="nav-menu flex-1 overflow-y-auto py-6">
            <ul className="space-y-2 px-3">
              {navigationItems.map((item) => {
                // Check if item should be shown based on role and permissions
                const shouldShow = isSuperAdmin() || (
                  (!item.requiresSuperAdmin) &&
                  (!item.requiredPermission || checkPermission(item.requiredPermission)) &&
                  (!item.excludedRoles || !item.excludedRoles.includes(userRole))
                );

                if (!shouldShow) return null;

                const isActive = currentPage === item.id || 
                  (item.id === 'dashboard' && currentPage === 'dashboard-supervisor' && userRole === USER_ROLES.DELIVERY_SUPERVISOR);

                const hasChildren = Array.isArray(item.children) && item.children.length > 0;
                const isExpanded = expandedMenu === item.id;

                return (
                  <li key={item.id} className="relative">
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => {
                          // If item has children (like Products), toggle submenu on click/tap regardless of collapse state.
                          if (hasChildren) {
                            // Special case for dashboard: navigate AND open submenu, but keep menu open
                            if (item.id === 'dashboard') {
                              const targetId = userRole === USER_ROLES.DELIVERY_SUPERVISOR ? 'dashboard-supervisor' : item.id;
                              if (setCurrentPage) setCurrentPage(targetId);
                              if (expandedMenu !== item.id) {
                                setExpandedMenu(item.id);
                                setManuallyToggled(item.id);
                                if (isCollapsed) setIsCollapsed(false);
                              }
                            } else {
                              const newExpanded = expandedMenu === item.id ? null : item.id;
                              setExpandedMenu(newExpanded);
                              setManuallyToggled(newExpanded);

                              // If sidebar is collapsed and we're opening a submenu, expand the sidebar
                              if (isCollapsed && newExpanded !== null) {
                                setIsCollapsed(false);
                              }
                            }
                          } else {
                            handleNavigation(item.id);
                          }
                        }}
                        className="w-full flex items-center space-x-3 px-4 py-3  group"
                        style={{
                          backgroundColor: isActive
                            ? colorHelpers.withOpacity(UI_COLORS.TEXT.ON_THEME, 0.2)
                            : 'transparent',
                          color: UI_COLORS.TEXT.ON_THEME,
                          ...(isActive && {
                            boxShadow: `0 4px 12px ${UI_COLORS.SHADOWS.ACCENT}`,
                            borderLeft: `3px solid ${UI_COLORS.NAVIGATION.ACTIVE}`
                          })
                        }}
                        onMouseEnter={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.ON_THEME, 0.1);
                            e.currentTarget.style.transform = 'translateX(4px)';
                          }
                          // Set hovered item for tooltip when collapsed
                          if (isCollapsed) {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setHoveredItem(item);
                            setHoveredItemPosition({ top: rect.top + rect.height / 2 });
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.transform = 'translateX(0px)';
                          }
                          // Clear hovered item
                          if (isCollapsed) {
                            setHoveredItem(null);
                            setHoveredItemPosition({ top: 0 });
                          }
                        }}
                        title={isCollapsed ? item.label : ''}
                      >
                        <FontAwesomeIcon
                          icon={item.icon}
                          className={`transition-all duration-200 ${isCollapsed ? 'text-base' : 'text-sm'}`}
                          style={{
                            color: isActive ? UI_COLORS.NAVIGATION.ACTIVE : 'inherit',
                            width: '16px'
                          }}
                        />
                        {!isCollapsed && (
                          <span className="text-sm font-medium tracking-wide">{item.label}</span>
                        )}
                      </button>

                      {hasChildren && !isCollapsed && (
                        <span className="ml-auto">
                          <FontAwesomeIcon
                            icon={faChevronRight}
                            className="w-3 h-3 transition-transform duration-300"
                            style={{
                              transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                              color: isActive ? UI_COLORS.NAVIGATION.ACTIVE : UI_COLORS.TEXT.ON_THEME,
                              opacity: 0.7
                            }}
                          />
                        </span>
                      )}
                    </div>
                    {/* Inline dropdown: render children directly under the parent when expanded */}
                    {hasChildren && isExpanded && (
                      <div className="ml-6 mr-3 mt-2">
                        <ul style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.TEXT.ON_THEME, 0.06) }}>
                          {item.children.filter(child => isSuperAdmin() || (
                            (!child.requiresSuperAdmin) &&
                            (!child.requiredPermission || checkPermission(child.requiredPermission)) &&
                            (!child.excludedRoles || !child.excludedRoles.includes(userRole))
                          )).map(child => {
                            const childActive = currentPage === child.id;
                            return (
                              <li key={child.id}>
                                <button
                                  onClick={() => {
                                    // If this child is already active, do nothing and keep submenu open
                                    if (childActive) return;
                                    // navigate to child page (use handleChildNavigation to keep submenu open)
                                    handleChildNavigation(child.id);
                                  }}
                                  className="w-full flex items-center space-x-3 px-3 py-2 rounded-md text-sm hover:bg-white/10"
                                  style={{
                                    color: UI_COLORS.TEXT.ON_THEME,
                                    ...(childActive && {
                                      backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.ON_THEME, 0.2),
                                      boxShadow: `0 4px 12px ${UI_COLORS.SHADOWS.ACCENT}`,
                                      borderLeft: `3px solid ${UI_COLORS.NAVIGATION.ACTIVE}`
                                    })
                                  }}
                                >
                                  <FontAwesomeIcon icon={child.icon} className="w-4 h-4" style={{ color: childActive ? UI_COLORS.NAVIGATION.ACTIVE : 'inherit' }} />
                                  <span className="truncate">{child.label}</span>
                                </button>
                              </li>
                            )
                          })}
                        </ul>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Warehouse Filter - Show for all authenticated users */}
          {!isCollapsed && user && (
            <div 
              className="px-6 py-4 border-t" 
              style={{ 
                borderColor: colorHelpers.withOpacity(UI_COLORS.TEXT.ON_THEME, 0.1),
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.ON_THEME, 0.02)
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-bold uppercase tracking-widest opacity-70" style={{ color: UI_COLORS.TEXT.ON_THEME }}>
                  Warehouse Filter
                </p>
                {selectedWarehouseIds.length > 0 && (
                  <button 
                    onClick={clearSelection}
                    className="text-[10px] font-semibold hover:underline opacity-80"
                    style={{ color: UI_COLORS.NAVIGATION.ACTIVE }}
                  >
                    Clear
                  </button>
                )}
              </div>
              <SearchableSelect
                isMulti={true}
                options={warehouses.map(w => ({ value: w.id, label: w.name }))}
                value={selectedWarehouseIds}
                onChange={(e) => setWarehouseIds(e.target.value)}
                placeholder="All Warehouses"
                loading={warehousesLoading}
                className="warehouse-filter-select"
              />
              <style>{`
                .warehouse-filter-select input {
                  padding-top: 0.5rem !important;
                  padding-bottom: 0.5rem !important;
                  font-size: 0.75rem !important;
                  border-radius: 0.75rem !important;
                  background-color: #ffffff !important;
                  border-color: rgba(255, 255, 255, 0.3) !important;
                  color: #1a202c !important;
                  font-weight: 600 !important;
                }
                .warehouse-filter-select input::placeholder {
                  color: #718096 !important;
                  opacity: 1 !important;
                }
                .warehouse-filter-select input:focus {
                  background-color: #ffffff !important;
                  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3) !important;
                }
              `}</style>
            </div>
          )}

          {/* Profile Button */}
          <div className="pr-4 pl-4 border-t" style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.TEXT.ON_THEME, 0.05) }}>
            <button
              onClick={() => handleNavigation('profile')}
              className="w-full flex items-center space-x-3 px-4 py-3 text-left rounded-xl transition-all duration-200 focus:outline-none group"
              style={{ color: UI_COLORS.TEXT.ON_THEME }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.ON_THEME, 0.05);
                e.currentTarget.style.transform = 'translateX(4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.transform = 'translateX(0px)';
              }}
              title={isCollapsed ? strings.sidebar.profile || 'Profile' : ''}
            >
              <FontAwesomeIcon
                icon={faUser}
                className="w-4 h-4"
                style={{ color: 'inherit' }}
              />
              {!isCollapsed && (
                <span className="text-sm font-medium">{strings.sidebar.profile || 'Profile'}</span>
              )}
            </button>
          </div>

          {/* Logout Button */}
          <div
            className="pr-4 pl-4 border-t"
            style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.TEXT.ON_THEME, 0.2) }}
          >
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-4 py-3 text-left rounded-xl transition-all duration-200 focus:outline-none group"
              style={{ color: UI_COLORS.STATUS.ERROR }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1);
                e.currentTarget.style.transform = 'translateX(4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.transform = 'translateX(0px)';
              }}
              title={isCollapsed ? strings.sidebar.logout : ''}
            >
              <FontAwesomeIcon
                icon={faSignOutAlt}
                className="w-4 h-4"
                style={{ color: 'inherit' }}
              />
              {!isCollapsed && (
                <span className="text-sm font-medium">{strings.sidebar.logout}</span>
              )}
            </button>
          </div>

          {/* Role Badge at Bottom */}
          {!isCollapsed && userRole && (
            <div className="p-4">
              <div
                className="px-4 py-2 rounded-xl text-xs font-semibold text-center shadow-lg"
                style={{
                  background: UI_COLORS.BACKGROUNDS.PRIMARY,
                  color: UI_COLORS.TEXT.PRIMARY,
                  textTransform: 'capitalize',
                  letterSpacing: '0.5px'
                }}
              >
                {t('users.forms.userTypes', { returnObjects: true })?.find(ut => ut.value === userRole)?.label || userRole.replace('_', ' ')}
              </div>
            </div>
          )}
          {/* Developer Credit - only show on mobile/tablet screens (not desktop) */}
          {!isCollapsed && (
            <div className="xl:hidden">
              <DeveloperCredit variant="sidebar" />
            </div>
          )}
        </div>
      </div>

      {/* Tooltip for collapsed navbar */}
      {isCollapsed && hoveredItem && !expandedMenu && (
        <div
          className="fixed z-50 px-3 py-2 text-sm font-medium text-white rounded-lg shadow-lg pointer-events-none"
          style={{
            backgroundColor: UI_COLORS.BACKGROUNDS.DARK || '#1f2937',
            left: '80px', // Position just outside the collapsed sidebar (64px width + 16px margin)
            top: hoveredItemPosition.top,
            transform: 'translateY(-50%)',
          }}
        >
          {hoveredItem.label}
          {/* Arrow pointing to sidebar */}
          <div
            className="absolute right-full top-1/2 transform -translate-y-1/2"
            style={{
              width: 0,
              height: 0,
              borderTop: '6px solid transparent',
              borderBottom: '6px solid transparent',
              borderRight: `6px solid ${UI_COLORS.BACKGROUNDS.DARK || '#1f2937'}`,
            }}
          />
        </div>
      )}

      {/* Floating submenu removed — inline dropdowns render within the nav list items now */}
    </>
  );
};

export default SideNavbar;